/**
 * ClientChat — AM-side chat component
 *
 * Shown inside the AM portal when an AM selects a client conversation.
 * Supports real-time messaging with read receipts.
 */

import { useState, useEffect, useRef } from "react";
import {
  Send,
  Loader2,
  Check,
  CheckCheck,
  User,
  Paperclip,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAMMessages, AMMessage, AMConversation } from "@/hooks/useAMMessages";
import { toast } from "sonner";

interface ClientChatProps {
  conversation: AMConversation;
  amId: string;
  senderId: string;
}

function formatMessageTime(ts: string): string {
  const date = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  if (diffDays === 0) return time;
  if (diffDays === 1) return `Yesterday ${time}`;
  if (diffDays < 7) {
    return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${time}`;
  }
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function ReadIndicator({ msg }: { msg: AMMessage }) {
  if (msg.sender_role !== "am") return null;
  if (msg.read_at) {
    return <CheckCheck className="w-3 h-3 text-blue-500" />;
  }
  // Message exists in DB = delivered; show single check until read
  return <Check className="w-3 h-3 text-muted-foreground" />;
}

export default function ClientChat({
  conversation,
  amId,
  senderId,
}: ClientChatProps) {
  const [body, setBody] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { messages, loading, sending, sendMessage, sendAttachment, markAsRead, unreadCount } =
    useAMMessages({
      amId,
      agentId: conversation.agent_id,
      senderRole: "am",
      senderId,
      enabled: true,
    });

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark incoming messages as read
  useEffect(() => {
    if (unreadCount > 0) {
      markAsRead();
    }
  }, [unreadCount, markAsRead]);

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || sending) return;

    const result = await sendMessage(trimmed);
    if (result) {
      setBody("");
    } else {
      toast.error("Failed to send message. Please try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }

    const result = await sendAttachment(file);
    if (!result) {
      toast.error("Failed to upload file. Please try again.");
    }
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clientInitials = getInitials(conversation.agent_name);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground font-semibold text-sm">
            {clientInitials}
          </div>
          <div>
            <p className="font-medium text-foreground">
              {conversation.agent_name}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {conversation.agent_company && (
                <span>{conversation.agent_company}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No messages yet with {conversation.agent_name}.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Send a welcome message to start the conversation.
            </p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${
                msg.sender_role === "am" ? "justify-end" : "justify-start"
              }`}
            >
              {msg.sender_role === "client" && (
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground flex-shrink-0 text-xs font-semibold mr-2 mt-1">
                  {clientInitials}
                </div>
              )}
              <div
                className={`max-w-[75%] rounded-xl px-3.5 py-2.5 ${
                  msg.sender_role === "am"
                    ? "bg-[#1B2A4A] text-white rounded-br-sm"
                    : "bg-muted text-foreground rounded-bl-sm"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap break-words">
                  {msg.body}
                </p>
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="mt-1.5 space-y-1">
                    {msg.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={att.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-1.5 text-xs rounded px-2 py-1 ${
                          msg.sender_role === "am"
                            ? "bg-white/10 text-white/80 hover:text-white"
                            : "bg-black/5 text-foreground/80 hover:text-foreground"
                        }`}
                      >
                        <FileText className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{att.file_name}</span>
                        <span className="text-[10px] flex-shrink-0">
                          ({Math.round(att.size_bytes / 1024)}KB)
                        </span>
                      </a>
                    ))}
                  </div>
                )}
                <div
                  className={`flex items-center gap-1 mt-1 ${
                    msg.sender_role === "am" ? "justify-end" : "justify-start"
                  }`}
                >
                  <span
                    className={`text-[10px] ${
                      msg.sender_role === "am"
                        ? "text-white/60"
                        : "text-muted-foreground"
                    }`}
                  >
                    {formatMessageTime(msg.created_at)}
                  </span>
                  <ReadIndicator msg={msg} />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Compose */}
      <div className="p-4 border-t border-border">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
          onChange={handleFileSelect}
        />
        <div className="flex gap-2">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="h-[44px] w-[44px] flex-shrink-0"
            title="Attach file"
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          <Textarea
            placeholder={`Message ${conversation.agent_name}...`}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 2000))}
            onKeyDown={handleKeyDown}
            className="min-h-[44px] max-h-32 resize-none text-sm"
            rows={1}
            maxLength={2000}
            disabled={sending}
          />
          <Button
            size="icon"
            onClick={handleSend}
            disabled={sending || !body.trim()}
            className="h-[44px] w-[44px] flex-shrink-0"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
        {body.trim() && (
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Ctrl+Enter to send &middot; {body.length}/2000
          </p>
        )}
      </div>
    </div>
  );
}
